// Business type options
export type BusinessType =
  | 'proprietorship'
  | 'partnership'
  | 'private_limited'
  | 'llp'
  | 'public_limited'
  | 'individual'
  | 'huf';

// Client status options
export type ClientStatus = 'active' | 'inactive' | 'suspended';

// Constants for dropdowns
export const BUSINESS_TYPES: { value: BusinessType; label: string }[] = [
  { value: 'proprietorship', label: 'Proprietorship' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'private_limited', label: 'Private Limited (Pvt Ltd)' },
  { value: 'llp', label: 'Limited Liability Partnership (LLP)' },
  { value: 'public_limited', label: 'Public Limited' },
  { value: 'individual', label: 'Individual' },
  { value: 'huf', label: 'Hindu Undivided Family (HUF)' },
];

export const CLIENT_STATUSES: { value: ClientStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

// Source options for how the client was acquired
export type PartySource = 'Digital' | 'BNI' | 'Consultant';

export const SOURCE_OPTIONS: { value: PartySource; label: string }[] = [
  { value: 'Digital', label: 'Digital' },
  { value: 'BNI', label: 'BNI' },
  { value: 'Consultant', label: 'Consultant' },
];

export interface PartyGroup {
  id: number;
  name: string;
  organization_id: number;
  created_at: string; // ISO date string
  updated_at?: string | null; // ISO date string
}

export interface PartyGroupCreate {
  name: string;
}

export interface PartyGroupUpdate {
  name?: string;
}

export interface Party {
  id: number;
  name: string;
  phone?: string | null;
  email?: string | null;
  gstin?: string | null;
  gst_type?: string | null; // e.g., "Registered", "Unregistered/Consumer"
  state?: string | null;
  billing_address?: string | null;
  shipping_address?: string | null;
  has_separate_shipping: boolean;
  geo_location?: string | null; // Geographic location (latitude, longitude)
  opening_balance: string | number; // Using string or number for precision
  opening_balance_date?: string | null; // ISO date string (YYYY-MM-DD)
  credit_limit_type: 'no_limit' | 'custom_limit';
  credit_limit_amount?: string | number | null; // Using string or number
  unallocated_credit?: string | number | null; // Unallocated credit from payment vouchers
  unallocated_debit?: string | number | null; // Unallocated debit from payment vouchers
  additional_fields?: Record<string, unknown> | null; // For custom fields defined in backend
  // Business information fields
  trade_name?: string | null; // Trading/brand name
  business_type?: BusinessType | null; // Pvt Ltd, LLP, Proprietorship, Individual, HUF
  registration_number?: string | null; // CIN/LLPIN
  // Tax identifiers
  pan?: string | null; // PAN number
  tan?: string | null; // TAN for TDS
  // Dates and fiscal year
  date_of_incorporation?: string | null; // Date of incorporation/establishment
  fiscal_year_start?: string | null; // Fiscal year start (MM-DD format)
  fiscal_year_end?: string | null; // Fiscal year end (MM-DD format)
  // Additional details
  city?: string | null; // City name
  pincode?: string | null; // Postal code
  contact_person?: string | null; // Primary contact name
  client_status?: ClientStatus | null; // active, inactive, suspended
  source?: PartySource | null; // How the client was acquired (Digital, BNI, Consultant)
  organization_id: number;
  primary_party_type?: string; // e.g., "Customer", "Vendor"
  party_group_id?: number | null;
  site_id?: number | null; // Optional site link
  responsible_party_id?: number | null; // Employee/manager responsible for this party
  source_party_id?: number | null; // Consultant who referred this party
  client_user_id?: number | null; // Client user ID if created
  client_pin?: string | null; // Hashed client PIN (only useful when returned from API)
  created_at: string; // ISO date string
  updated_at?: string | null; // ISO date string
  party_group?: PartyGroup | null; // Optional relation for display
  site?: { id: number; name: string } | null; // Optional relation for display
  responsible_party?: { id: number; name: string } | null; // Optional relation for display
  source_party?: { id: number; name: string } | null; // Optional relation for display
}

export interface PartyUser {
  id: number;
  username: string;
  full_name?: string;
  email?: string;
  is_active: boolean;
}

export interface PartyCreate extends Omit<Party, 'id' | 'organization_id' | 'created_at' | 'updated_at' | 'party_group' | 'site' | 'responsible_party' | 'source_party'> {
  // Inherits fields from Party, excluding backend-generated ones and relations
}

export interface PartyUpdate extends Partial<Omit<PartyCreate, 'opening_balance' | 'opening_balance_date'>> {
  // Allows partial updates, potentially disallowing changes to opening balance via standard update
} 